import { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { LogOut, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useBranding } from "@/hooks/useBranding";

interface SubscriptionGateLayoutProps {
  children: ReactNode;
}

export const SubscriptionGateLayout = ({ children }: SubscriptionGateLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const brand = useBranding();
  const isOnBilling = location.pathname === "/billing";

  const handleLogout = async () => {
    // Clear storage first (resilient to expired sessions)
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.error("Error clearing storage:", e);
    }

    toast.success("Signed out successfully");

    // Fire-and-forget server signOut
    supabase.auth.signOut().catch(() => {});

    navigate("/auth", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="h-16 border-b border-border flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-3">
          {isOnBilling && (
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="mr-1">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img src={brand.logoLight} alt={brand.productName} className="h-8 w-8" />
            <span className="font-semibold text-lg text-foreground">{brand.productName}</span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-2">
          <LogOut className="h-4 w-4" />
          Logout
        </Button>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        {children}
      </main>
    </div>
  );
};
